/**
 * Competitions Tab — active/upcoming/ended competitions.
 * Spec: challenges .txt § 5.3.
 */

import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Trophy } from 'lucide-react'
import { BrowseChallengeCard } from './browse-challenge-card'
import { useCompetitionsList } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'

type CompSection = 'active' | 'upcoming' | 'completed'

export const CompetitionsTab = memo(function CompetitionsTab(): React.ReactElement {
  const { t } = useTranslation()
  const [section, setSection] = useState<CompSection>('active')
  const [page, setPage] = useState(0)
  const { data: competitions, isLoading } = useCompetitionsList(section, page)

  const handleLoadMore = useCallback((): void => {
    setPage((p) => p + 1)
  }, [])

  return (
    <div className="space-y-4">
      {/* Section toggle */}
      <div className="flex gap-1.5">
        {(['active', 'upcoming', 'completed'] as const).map((s) => (
          <button
            key={s}
            onClick={() => { setSection(s); setPage(0) }}
            className={`flex-1 px-2 py-2 rounded-xl text-[11px] font-semibold transition-colors tap-highlight-transparent ${
              section === s
                ? 'bg-primary/15 text-primary border border-primary/30'
                : 'bg-white/[0.03] text-text-hint border border-white/[0.06] hover:bg-white/[0.05]'
            }`}
          >
            {s === 'active' ? t('challenges.browse.ongoing')
              : s === 'upcoming' ? t('challenges.browse.upcoming')
              : t('challenges.browse.ended')}
          </button>
        ))}
      </div>

      {/* Competition list */}
      {isLoading && page === 0 ? (
        <div className="space-y-2.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[100px] rounded-2xl" />
          ))}
        </div>
      ) : (competitions?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<Trophy size={28} className="text-text-hint" />}
          title={t('challenges.browse.noResults')}
          description={t('challenges.browse.noResultsDesc')}
        />
      ) : (
        <>
          <div className="space-y-2.5">
            {(competitions ?? []).map((c) => (
              <BrowseChallengeCard key={c.id} challenge={c} />
            ))}
          </div>

          {(competitions?.length ?? 0) >= 20 && (
            <button
              onClick={handleLoadMore}
              className="w-full py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-text-hint font-medium hover:bg-white/[0.05] transition-colors tap-highlight-transparent"
            >
              {t('challenges.browse.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  )
})
